import { useLogin } from "@refinedev/core";
import { message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import {
  SCALEKIT_ENV_URL,
  SCALEKIT_CLIENT_ID,
  SCALEKIT_REDIRECT_URI,
  SCALEKIT_RESPONSE_TYPE,
  SCALEKIT_SCOPE,
} from "./config";
import {
  buildScalekitAuthorizeUrl,
  exchangeScalekitCode,
} from "@libertify/auth/core";
import { LoadingSpinner } from "components/LoadingSpinner/";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import {
  clearPendingAuthMethod,
  setPendingAuthMethod,
} from "services/pendingAuthMethod";
import { restoreSerializedAttribution } from "utils/analytics";
const API_URL = import.meta.env.VITE_API_BASE_URL;

export const ScalekitCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const { trackEvent } = useAnalytics();
  const { mutate: login } = useLogin({
    mutationOptions: {
      onError: () => {
        trackEvent(AnalyticsEvents.LoginFailed, {
          login_method: "scalekit",
          reason: "oauth_error",
        });
      },
    },
  });
  const navigate = useNavigate();

  // Handle IdP-initiated login (enterprise login from Okta, Azure AD, etc.)
  const handleIdpInitiated = (idpToken: string) => {
    trackEvent(AnalyticsEvents.LoginAttempt, { login_method: "scalekit" });
    clearPendingAuthMethod();
    setPendingAuthMethod("scalekit");

    // Redirect to ScaleKit to validate the IdP token and return a normal authorization code
    globalThis.location.href = buildScalekitAuthorizeUrl(
      {
        envUrl: SCALEKIT_ENV_URL,
        clientId: SCALEKIT_CLIENT_ID,
        redirectUri: SCALEKIT_REDIRECT_URI,
        responseType: SCALEKIT_RESPONSE_TYPE,
        scope: SCALEKIT_SCOPE,
      },
      { idpInitiatedLogin: idpToken }
    );
  };
  // Handle SSO initiated login returning a code from ScaleKit
  const handleSpInitiated = async (code: string) => {
    try {
      const data = await exchangeScalekitCode({ apiUrl: API_URL, code });

      login({
        provider: "scalekit",
        token: data,
        redirect: true,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "An unexpected error occurred";
      trackEvent(AnalyticsEvents.LoginFailed, {
        login_method: "scalekit",
        reason: "oauth_error",
      });
      message.error("Error: " + errorMessage);
      navigate("/login");
    }
  };

  useEffect(() => {
    const code = searchParams.get("code");
    const error = searchParams.get("error");
    const idpToken = searchParams.get("idp_initiated_login");

    if (error) {
      trackEvent(AnalyticsEvents.LoginFailed, {
        login_method: "scalekit",
        reason: "oauth_error",
      });
      message.error("Error: " + error);
      navigate("/login");
      return;
    }
    if (idpToken) {
      handleIdpInitiated(idpToken);
      return;
    }
    if (code) {
      // when it returns from ScaleKit to our app
      // Restore before login() so identify gets the UTMs and ICP type
      const state = searchParams.get("state");
      if (state) restoreSerializedAttribution(state);
      void handleSpInitiated(code);
      return;
    }
    // something went wrong
    navigate("/login");
  }, []);

  return <LoadingSpinner />;
};
